import type { SpacingToken } from '@/theme/tokens';
import type { Theme } from '@/ui/primitives';

type ThemeTypographyResolver = (theme: Theme) => string | number;

type TypographyTextSx = {
  fontSize?: ThemeTypographyResolver;
  lineHeight?: ThemeTypographyResolver;
};

type RootTypographyTextSx = TypographyTextSx;

type TypographySelectorSx = Record<string, TypographyTextSx>;

type NestedTypographySelectorSx = {
  '&&': TypographyTextSx;
};

type SurfaceBackgroundSx = {
  backgroundColor: 'background.paper';
};

const resolveH2FontSize = (theme: Theme): string | number =>
  theme.typography.h2.fontSize ?? 'inherit';
const resolveH2LineHeight = (theme: Theme): string | number =>
  theme.typography.h2.lineHeight ?? 'normal';
const resolveH3FontSize = (theme: Theme): string | number =>
  theme.typography.h3.fontSize ?? 'inherit';
const resolveH3LineHeight = (theme: Theme): string | number =>
  theme.typography.h3.lineHeight ?? 'normal';
const resolveSubtitle1FontSize = (theme: Theme): string | number =>
  theme.typography.subtitle1.fontSize ?? 'inherit';
const resolveSubtitle1LineHeight = (theme: Theme): string | number =>
  theme.typography.subtitle1.lineHeight ?? 'normal';
const resolveSubtitle2FontSize = (theme: Theme): string | number =>
  theme.typography.subtitle2.fontSize ?? 'inherit';
const resolveSubtitle2LineHeight = (theme: Theme): string | number =>
  theme.typography.subtitle2.lineHeight ?? 'normal';

export const settingsPageTypographySx: TypographySelectorSx = {
  '& .MuiTypography-h1': {
    fontSize: (theme: Theme) => resolveH2FontSize(theme),
    lineHeight: (theme: Theme) => resolveH2LineHeight(theme),
  },
  '& .MuiTypography-body1:not(label)': {
    lineHeight: (theme: Theme) => resolveSubtitle1LineHeight(theme),
  },
  '& .MuiTypography-h4': {
    fontSize: (theme: Theme) => resolveH3FontSize(theme),
    lineHeight: (theme: Theme) => resolveH3LineHeight(theme),
  },
};

export const settingsCopyTextSx: NestedTypographySelectorSx = {
  '&&': {
    lineHeight: (theme: Theme) => resolveSubtitle1LineHeight(theme),
  },
};

export const settingsValueTextSx = settingsCopyTextSx;

export const settingsLabelTextSx: NestedTypographySelectorSx = {
  '&&': {
    fontSize: (theme: Theme) => resolveSubtitle2FontSize(theme),
    lineHeight: (theme: Theme) => resolveSubtitle2LineHeight(theme),
  },
};

export const planSettingsLimitsLabelTextSx: NestedTypographySelectorSx = {
  '&&': {
    lineHeight: (theme: Theme) => resolveSubtitle2LineHeight(theme),
  },
};

export const planSettingsLimitsHeadingTextSx: RootTypographyTextSx = {
  fontSize: (theme: Theme) => resolveSubtitle1FontSize(theme),
  lineHeight: (theme: Theme) => resolveSubtitle2LineHeight(theme),
};

export const settingsPanelSurfaceSx: SurfaceBackgroundSx = {
  backgroundColor: 'background.paper',
};

export const settingsInputBackgroundColor = 'background.default' as const;

export const settingsPanelsGap: SpacingToken = 'xl';
